<template>
  <div class="fortune-wheel">
    <div class="fortune-wheel__wrap">
      <svg
        class="fortune-wheel__svg"
        viewBox="0 0 200 200"
        :style="{ transform: `rotate(${rotation}deg)` }"
      >
        <defs>
          <linearGradient
            v-for="(c, i) in gradientColors"
            :key="i"
            :id="`seg-${i}`"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" :stop-color="c.light" />
            <stop offset="100%" :stop-color="c.dark" />
          </linearGradient>
        </defs>
        <g v-for="(seg, i) in segments" :key="i">
          <path
            :d="seg.path"
            :fill="`url(#seg-${i})`"
            stroke="#fff"
            stroke-width="1.2"
          />
          <g :transform="seg.textTransform">
            <text
              class="fortune-wheel__text"
              x="0"
              y="0"
              text-anchor="middle"
              dominant-baseline="middle"
            >
              <tspan v-for="(line, li) in seg.lines" :key="li" :dy="li === 0 ? 0 : 9" x="0">
                {{ line }}
              </tspan>
            </text>
          </g>
        </g>
      </svg>
      <div class="fortune-wheel__pointer" />
      <button
        v-if="!hasFinished"
        type="button"
        class="fortune-wheel__btn"
        :disabled="isSpinning || !items.length"
        @click="spin"
      >
        {{ isSpinning ? '...' : 'Крутить' }}
      </button>
      <div v-if="hasFinished && winnerLabel" class="fortune-wheel__winner">
        <span class="fortune-wheel__winner-text">{{ winnerLabel }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import confetti from 'canvas-confetti'
const COLORS = [
  { light: '#f97316', dark: '#c2410c' },
  { light: '#22c55e', dark: '#15803d' },
  { light: '#3b82f6', dark: '#1d4ed8' },
  { light: '#eab308', dark: '#a16207' },
  { light: '#ec4899', dark: '#be185d' },
  { light: '#8b5cf6', dark: '#6d28d9' },
  { light: '#14b8a6', dark: '#0f766e' },
  { light: '#f97316', dark: '#c2410c' },
  { light: '#22c55e', dark: '#15803d' },
  { light: '#3b82f6', dark: '#1d4ed8' },
]

function splitLabel(label, maxChars = 12) {
  const s = String(label || '').trim()
  if (!s) return ['']
  if (s.length <= maxChars) return [s]
  const mid = Math.ceil(s.length / 2)
  let split = mid
  const space = s.indexOf(' ', mid - 4)
  if (space > mid - 6 && space < mid + 6) split = space + 1
  return [s.slice(0, split).trim(), s.slice(split).trim()].filter(Boolean)
}

function polarToCart(cx, cy, r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

function describeDonutSegment(cx, cy, rOut, rIn, startDeg, endDeg) {
  const startOut = polarToCart(cx, cy, rOut, startDeg)
  const endOut = polarToCart(cx, cy, rOut, endDeg)
  const endIn = polarToCart(cx, cy, rIn, endDeg)
  const startIn = polarToCart(cx, cy, rIn, startDeg)
  const large = endDeg - startDeg > 180 ? 1 : 0
  return `M ${startOut.x} ${startOut.y} A ${rOut} ${rOut} 0 ${large} 1 ${endOut.x} ${endOut.y} L ${endIn.x} ${endIn.y} A ${rIn} ${rIn} 0 ${large} 0 ${startIn.x} ${startIn.y} Z`
}

export default {
  name: 'FortuneWheel',
  props: {
    items: { type: Array, default: () => [] },
  },
  emits: ['finished'],
  data() {
    return {
      isSpinning: false,
      hasFinished: false,
      rotation: 0,
      winnerLabel: '',
    }
  },
  computed: {
    gradientColors() {
      const n = this.items.length
      return n ? [...Array(n)].map((_, i) => COLORS[i % COLORS.length]) : []
    },
    segments() {
      const n = this.items.length
      if (!n) return []
      const cx = 100
      const cy = 100
      const rOut = 98
      const rIn = 28
      const step = 360 / n
      const textR = (rOut + rIn) / 2 - 4
      return this.items.map((item, i) => {
        const start = i * step
        const end = start + step
        const mid = (start + end) / 2
      const path = describeDonutSegment(cx, cy, rOut, rIn, start, end)
        const lines = splitLabel(item.label)
        const p = polarToCart(cx, cy, textR, mid)
        // поперёк сегмента (по касательной): +90°; в нижней половине переворачиваем
        const rot = mid > 90 && mid < 270 ? mid - 90 : mid + 90
        const textTransform = `translate(${p.x} ${p.y}) rotate(${rot})`
        return { path, lines, textTransform, mid, item }
      })
    },
  },
  methods: {
    // Сегмент, который после rotation оказывается под указателем (верх = 0°)
    getSegmentAtPointer(rotation) {
      if (!this.segments.length) return null
      const r = ((rotation % 360) + 360) % 360
      const topMid = (360 - r) % 360
      let best = this.segments[0]
      let bestDiff = 360
      for (const s of this.segments) {
        const m = ((s.mid % 360) + 360) % 360
        let diff = Math.abs(m - topMid)
        if (diff > 180) diff = 360 - diff
        if (diff < bestDiff) {
          bestDiff = diff
          best = s
        }
      }
      return best
    },
    spin() {
      if (!this.items.length || this.isSpinning || this.hasFinished) return
      this.isSpinning = true
      const idx = Math.floor(Math.random() * this.items.length)
      const seg = this.segments[idx]
      const turns = 5 + Math.random() * 2
      const target = 360 * turns - seg.mid
      this.rotation = target
      const duration = 4200
      setTimeout(() => {
        this.isSpinning = false
        // Приз берём по фактическому углу остановки — сегмент под указателем
        const winnerSegment = this.getSegmentAtPointer(this.rotation)
        const label = winnerSegment ? winnerSegment.item.label : seg.item.label
        this.winnerLabel = label || ''
        this.hasFinished = true
        this.$emit('finished', label)
        this.fireConfetti()
      }, duration)
    },
    fireConfetti() {
      const count = 120
      const defaults = { origin: { y: 0.6 }, zIndex: 9999 }
      const fire = (opts) => confetti({ ...defaults, ...opts })
      fire({ particleCount: count, spread: 100 })
      fire({ particleCount: count * 0.6, spread: 80, origin: { x: 0.2, y: 0.5 } })
      fire({ particleCount: count * 0.6, spread: 80, origin: { x: 0.8, y: 0.5 } })
      fire({ particleCount: count * 0.4, spread: 60, origin: { x: 0.5, y: 0.3 }, startVelocity: 45 })
      setTimeout(() => fire({ particleCount: count * 0.5, angle: 60, spread: 55, origin: { x: 0 } }), 200)
      setTimeout(() => fire({ particleCount: count * 0.5, angle: 120, spread: 55, origin: { x: 1 } }), 400)
    },
  },
}
</script>

<style scoped lang="scss">
@import "../../styles/_variables.scss";

.fortune-wheel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.fortune-wheel__wrap {
  position: relative;
  width: 280px;
  height: 280px;
  flex-shrink: 0;
}

.fortune-wheel__svg {
  width: 100%;
  height: 100%;
  transition: transform 4.2s cubic-bezier(0.17, 0.67, 0.12, 0.99);
  transform-origin: 50% 50%;
}

.fortune-wheel__text {
  fill: #fff;
  font-size: 8px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  pointer-events: none;
}

.fortune-wheel__pointer {
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 12px solid transparent;
  border-right: 12px solid transparent;
  border-top: 20px solid #f97316;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4));
  z-index: 2;
  pointer-events: none;
}

.fortune-wheel__btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 3px solid #0f172a;
  background: linear-gradient(145deg, #fbbf24, #f59e0b);
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  z-index: 3;
  transition: transform 0.15s, box-shadow 0.15s;
}

.fortune-wheel__btn:disabled {
  opacity: 0.8;
  cursor: default;
}

.fortune-wheel__btn:not(:disabled):hover {
  transform: translate(-50%, -50%) scale(1.05);
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.5);
}

.fortune-wheel__btn:not(:disabled):active {
  transform: translate(-50%, -50%) scale(0.98);
}

.fortune-wheel__winner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: linear-gradient(145deg, #22c55e, #15803d);
  border: 3px solid #14532d;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  z-index: 3;
  box-shadow: 0 4px 20px rgba(34, 197, 94, 0.5);
  animation: fortune-wheel-winner-in 0.4s ease-out;
}

.fortune-wheel__winner-text {
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  word-break: break-word;
  display: block;
  width: 100%;
}

@keyframes fortune-wheel-winner-in {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.6);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

@media (min-width: 400px) {
  .fortune-wheel__wrap {
    width: 320px;
    height: 320px;
  }

  .fortune-wheel__text {
    font-size: 9px;
  }

  .fortune-wheel__winner {
    width: 100px;
    height: 100px;
    padding: 12px;
  }

  .fortune-wheel__winner-text {
    font-size: 34px;
  }
}
</style>
